
// src/lib/useAuthUser.js
import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Hook: waits for Firebase Auth to hydrate the user.
 * status: 'loading' | 'signed-in' | 'signed-out'
 */
export function useAuthUser() {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        if (typeof window === "undefined") {
            setUser(null);
            setStatus("signed-out");
            return;
        }
        const unsub = onAuthStateChanged(auth, (u) => {
            if (u) {
                setUser(u);
                setStatus("signed-in");
            } else {
                setUser(null);
                setStatus("signed-out");
            }
        });
        return () => unsub();
    }, []);

    return { user, status };
}

/**
 * Subscribe to /users/{uid} to read role and serviceCategory.
 */
export function useUserDoc(uid) {
    const [userDoc, setUserDoc] = useState(null);
    const [loading, setLoading] = useState(!!uid);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!uid) {
            setUserDoc(null);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        const ref = doc(db, "users", uid);
        const unsub = onSnapshot(
            ref,
            (snap) => {
                setUserDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
                setLoading(false);
            },
            (err) => {
                setError(err);
                setLoading(false);
            }
        );
        return () => unsub();
    }, [uid]);

    return { userDoc, loading, error };
}

/** Convenience helper: prefer /users/{uid}.serviceCategory, else employee doc’s category */
export function deriveCategoryKey({ userDoc, employeeDoc }) {
    return (
        userDoc?.serviceCategory ||
        employeeDoc?.categoryKey ||
        (employeeDoc?.jobs && employeeDoc.jobs[0]?.categoryKey) ||
        null
    );
}